class Inits {
    constructor(engine) {
        this.preCacheGraphics = {};
        this.graphics = {};
        this.heads = {};
        this.bodies = {};
        this.armas = {};
        this.escudos = {};
        this.cascos = {};
        this.objs = {};
        this.mapData = {};
        this.mapa = {};

        this.mapasCargados = 0;
        this.completedCount = 0;

        this.loaded = false;

        this.react = "";
    }

    setReact = react => {
        this.react = react;
    };

    initialize = async () => {
        if (!this.loaded) {
            await Promise.all([
                this.loadCascos(),
                this.loadHeads(),
                this.loadArmas(),
                this.loadEscudos(),
                this.loadBodies(),
                this.loadGraphics(),
                this.loadWorldmap(),
                this.loadTileExits(),
                this.loadHostileSpawns(),
                this.loadHostileCatalog()
            ]);

            this.loaded = true;
        }

        console.log("Loaded", this.loaded);
    };

    // Worldmap.json: chico (~6 KB) — usado para resolver vecinos en
    // prefetch background tras cargar un mapa. Sin esto el prefetch
    // solo conoce vecinos via tileExits del mapa actual.
    loadWorldmap = async () => {
        try {
            const response = await fetch("/static/init/worldmap.json");
            this.worldmap = await response.json();
        } catch (e) {
            // No es fatal — el lazy-load sigue funcionando sin prefetch.
            this.worldmap = null;
        }
    };

    // tileExits.json: shape { sourceMap: [targetMap, ...], ... }.
    // ~5 KB. Generado server-side desde mapa_N.json scaneando los
    // c.tileExit.map de cada celda. Necesario porque los .map files
    // del cliente (versión visual del mapa) NO incluyen tileExits —
    // solo tiles + decoración. Sin este file el prefetch no sabe que
    // map 1 (Ullathorpe) tiene tileExit a map 40 (interior del banco)
    // y por lo tanto no lo precargaría.
    //
    // El archivo se regenera corriendo el snippet de docker exec en
    // server/mapas/*.json (tools en _reference/). Si el server agrega
    // tileExits nuevas hay que regenerar este file.
    loadTileExits = async () => {
        try {
            const response = await fetch("/static/init/tileExits.json");
            this.tileExits = await response.json();
        } catch (e) {
            this.tileExits = null;
        }
    };

    // ── Lazy map loading ──────────────────────────────────────────
    // Antes (legacy): boot bloqueaba con Promise.all sobre 290 fetches
    // = ~75 MB de JSON parseado en main thread → 5-30s de espera ANTES
    // de poder jugar. Ahora cargamos solo bajo demanda:
    //   - getMyCharacter espera `ensureMapLoaded(user.map)` antes de
    //     arrancar el engine.
    //   - telepMe espera `ensureMapLoaded(numMap)` antes de respawnMe.
    //   - Cache permanente en this.mapa[N] / this.mapData[N] (typical
    //     session: 10-30 mapas visitados = 2-7 MB vs los 75 originales).
    //   - Prefetch background de vecinos via worldmap.json + tileExits
    //     del mapa actual → transiciones instantáneas en uso normal.
    //
    // Boot ahora carga 0 mapas. Total init data baja de ~75 MB a ~5 MB.
    loadMaps = async () => {
        // Compat con UI loader — show "listo" inmediatamente. Si el user
        // está en typeGame=2 (PvP arena), prefetch el mapa 272 ya que es
        // el único; sino el primer mapa vendrá del getMyCharacter.
        const typeGame = parseInt(sessionStorage.getItem("typeGame"));
        this.react.setState({ mapasToLoad: 1, mapasCargados: 1 });

        if (typeGame === 2) {
            // PvP arena — único mapa, prefetch para que el connect sea
            // instantáneo.
            return inits.ensureMapLoaded(272);
        }
        // PvE — no prefetch al boot. Vamos a esperar a saber qué mapa
        // tiene el char (getMyCharacter packet) y cargar ahí.
    };

    // Cache de Promises en flight para dedupe — si dos paths piden
    // el mismo mapa simultáneamente, esperan al mismo fetch.
    _mapLoadPromises = {};

    // API principal — devuelve Promise que resuelve cuando el mapa
    // está disponible en this.mapa[N] + this.mapData[N]. Idempotente.
    ensureMapLoaded = mapNum => {
        if (this.mapa[mapNum]) {
            // Cache hit — ya está cargado.
            return Promise.resolve();
        }
        if (this._mapLoadPromises[mapNum]) {
            // In flight — esperá al fetch que ya empezó.
            return this._mapLoadPromises[mapNum];
        }
        this._mapLoadPromises[mapNum] = this.loadMap(mapNum)
            .then(() => {
                // Eliminar del registro de in-flight tras éxito —
                // futuras llamadas usan el cache de this.mapa[N].
                delete this._mapLoadPromises[mapNum];
            })
            .catch(err => {
                // Si falló, removemos del registro para permitir retry.
                delete this._mapLoadPromises[mapNum];
                throw err;
            });
        return this._mapLoadPromises[mapNum];
    };

    loadMap = async map => {
        const response = await fetch("/static/mapas/mapa_" + map + ".map");
        const result = await response.json();

        this.mapa[map] = result[map];

        this.createMapData(map);

        // CRÍTICO para smoothness: preload de los tile sprites usados
        // por este mapa. Sin esto el engine intenta renderear, encuentra
        // preCacheGraphics[numFile] undefined, dispara loadImage async
        // y deja el canvas en negro hasta que las imágenes lleguen
        // (~500-1500ms de "black screen flash" en cada transición).
        //
        // Awaitamos las imágenes ANTES de marcar el mapa como listo —
        // de esta forma cuando respawnMe corre, los tile sprites ya
        // están en memoria y el render es smooth.
        await this.preloadMapImages(map);

        this.mapasCargados++;

        if (this.react) {
            this.react.setState({
                mapasCargados: this.mapasCargados
            });
        }
    };

    // Preload todas las imágenes (PNGs) que el mapa usa para sus tile
    // sprites. Escanea las 10000 cells del .map, recolecta los grhIds
    // de cada layer (g[1]..g[4]), resuelve a numFiles vía inits.graphics
    // y arranca un loadImage por cada uno (deduplica los ya cacheados).
    //
    // Para mapas grandes con muchas decoraciones unique: ~50-100 imágenes.
    // Muchas son shared (grass, agua, paredes de ciudad) — el preCache
    // global garantiza que cada imagen se carga UNA SOLA VEZ en la sesión.
    preloadMapImages = async map => {
        const mapData = this.mapa[map];
        if (!mapData) return;
        const graphics = this.graphics;
        if (!graphics) return;

        // Recolectar numFiles únicos.
        const numFiles = new Set();
        for (let y = 1; y <= 100; y++) {
            const row = mapData[y];
            if (!row) continue;
            for (let x = 1; x <= 100; x++) {
                const cell = row[x];
                if (!cell || !cell.g) continue;
                for (const layer in cell.g) {
                    const grhId = cell.g[layer];
                    if (!grhId) continue;
                    const grh = graphics[grhId];
                    if (!grh) continue;
                    if (grh.numFile) numFiles.add(grh.numFile);
                    // Animaciones tienen frames[1..N] → cada frame puede
                    // referenciar otro grhId con su numFile.
                    if (grh.frames) {
                        for (const f in grh.frames) {
                            const subId = grh.frames[f];
                            const sub = graphics[subId];
                            if (sub && sub.numFile) numFiles.add(sub.numFile);
                        }
                    }
                }
            }
        }

        // Disparar loadImage en paralelo. preCacheGraphics ya tiene
        // dedupe (chequea ¡ya cacheado? antes de fetch). Si la image
        // está cacheada, loadImage resuelve instant — no re-fetch.
        const promises = [];
        for (const numFile of numFiles) {
            if (this.preCacheGraphics[numFile]) continue;
            promises.push(this.loadImage(numFile).catch(() => {}));
        }
        await Promise.all(promises);
    };

    createMapData = idMap => {
        this.mapData[idMap] = [];

        for (var y = 1; y <= 100; y++) {
            this.mapData[idMap][y] = [];

            for (var x = 1; x <= 100; x++) {
                this.mapData[idMap][y][x] = {
                    id: 0
                };
            }
        }
    };

    // Prefetch background: dispara fetches paralelos sin esperar. Se
    // llama post-respawnMe para que la próxima transición sea instant.
    // No-op si el mapa ya está cacheado (ensureMapLoaded cortocircuita).
    prefetchMaps = mapNums => {
        if (!Array.isArray(mapNums)) return;
        for (const m of mapNums) {
            if (!m || typeof m !== "number") continue;
            if (m < 1 || m > 290) continue;
            this.ensureMapLoaded(m).catch(() => {});
        }
    };

    // Devuelve los map numbers vecinos en el worldmap (N/S/E/W) + los
    // referidos por tileExits del mapa. Combina 2 fuentes:
    //
    //   - worldmap.json: adyacencias visuales del mapamundi (los 4 N/S/E/W).
    //     Best-effort — tiene ~175 mismatches conocidos vs server (#241
    //     follow-up), pero igual cubre bien las macro-zonas.
    //
    //   - tileExits.json: lista canónica server-side de a dónde lleva cada
    //     tileExit. CRÍTICO: los .map files del cliente NO incluyen
    //     tileExits (solo terreno visual), así que SIN este file el
    //     cliente no sabe que mapa 1 lleva al 40 vía el banco.
    getPrefetchCandidates = mapNum => {
        const candidates = new Set();

        // 1) Vecinos del worldmap (mapamundi visual).
        const wm = this.worldmap;
        if (wm && wm.worlds) {
            for (const wKey in wm.worlds) {
                const wd = wm.worlds[wKey];
                if (!wd || !wd.cells) continue;
                const idx = wd.cells.indexOf(mapNum);
                if (idx < 0) continue;
                const x = idx % wd.ancho;
                const y = Math.floor(idx / wd.ancho);
                const dirs = [
                    y > 0 ? wd.cells[(y - 1) * wd.ancho + x] : 0,
                    y < wd.alto - 1 ? wd.cells[(y + 1) * wd.ancho + x] : 0,
                    x > 0 ? wd.cells[y * wd.ancho + x - 1] : 0,
                    x < wd.ancho - 1 ? wd.cells[y * wd.ancho + x + 1] : 0
                ];
                for (const d of dirs) if (d) candidates.add(d);
                break;
            }
        }

        // 2) tileExits del mapa — fuente canónica server-side.
        // Cubre entradas a interiores (bancos, casas, dungeons) que NO
        // son adyacentes en el worldmap visual pero sí accesibles via
        // tileExit (puertas, escaleras, portales).
        if (this.tileExits && this.tileExits[mapNum]) {
            for (const target of this.tileExits[mapNum]) {
                if (target) candidates.add(target);
            }
        }

        return Array.from(candidates);
    };

    // Wrapper que combina ensureMapLoaded + prefetch background.
    // Estrategia anti-freeze para SMOOTHNESS máxima:
    //
    //   1. Cargo el mapa actual con AWAIT — JSON + tile sprites.
    //      Cuando esto resuelve, todo lo que el engine necesita para
    //      renderear ese mapa está en memoria. Renderbackground es
    //      instant sin flash de "black screen".
    //
    //   2. Ring 1 (vecinos worldmap + tileExits del actual): en LOGIN
    //      awaiteamos para que la PRIMERA transición sea instant; en
    //      TELEP disparamos en background (el prefetch del mapa
    //      anterior ya los cacheó en el 90%+ de los casos).
    //
    // SACADO: depth-2 ring. Era duplicación de tráfico — el momento
    // que crucés a un vecino, su loadCurrentMapAndPrefetch arranca SU
    // propio ring1 prefetch. Total efecto neto idéntico, mitad de
    // requests netos en idle.
    //
    // Lo que NO se prefetchea: data dinámica (NPCs, drops, items en
    // el piso, players, dialogs). Viene del WS server al entrar via
    // setNewAreas + renderItem + sendCharacter + sendNpc.
    loadCurrentMapAndPrefetch = async (mapNum, awaitRing1) => {
        await this.ensureMapLoaded(mapNum);

        const ring1 = this.getPrefetchCandidates(mapNum);

        if (awaitRing1) {
            // LOGIN path — bloqueamos hasta que TODOS los vecinos estén
            // listos (JSON + tile sprites). Login agrega ~1-2s vs el
            // no-await pero garantiza que la PRIMERA transición desde
            // el spawn sea garantizada instant + smooth (sin black flash).
            await Promise.all(
                ring1.map(m => this.ensureMapLoaded(m).catch(() => {}))
            );
        } else {
            // TELEP path — background fire-and-forget. Si el ring1 ya
            // fue cacheado por el prefetch del mapa anterior, este call
            // es no-op (dedupe en ensureMapLoaded). Si no, los fetches
            // arrancan ahora y la PRÓXIMA transición desde este mapa
            // será smooth.
            this.prefetchMaps(ring1);
        }
    };

    loadImage = numFile => {
        return new Promise((resolve, reject) => {
            var image = new Image();

            image.src = "/static/graficos/" + numFile + ".png";

            image.onload = () => {
                inits.preCacheGraphics[numFile] = image;

                resolve(true);
            };

            image.onerror = e => {
                reject(true);
            };
        });
    };

    loadObjs = async () => {
        const response = await fetch("/static/init/objs.json");
        const result = await response.json();

        this.objs = result;
    };

    loadHeads = async () => {
        const response = await fetch("/static/init/heads.json");
        const result = await response.json();

        this.heads = result;
    };

    loadBodies = async () => {
        const response = await fetch("/static/init/bodies.json");
        const result = await response.json();

        this.bodies = result;
    };

    loadGraphics = async () => {
        const response = await fetch("/static/init/graficos.json");
        const result = await response.json();

        this.graphics = result;
    };

    // Lista de spawns hostiles por mapa, shape { mapNum: [[y,x,npcIndex]...] }.
    // Usado por el FullMap modal para listar enemigos del mapa abierto.
    // Generado por `scripts/gen-hostile-catalog.js` (junto con hostileCatalog).
    loadHostileSpawns = async () => {
        try {
            const response = await fetch("/static/init/hostileSpawns.json");
            this.hostileSpawns = await response.json();
        } catch (e) {
            this.hostileSpawns = {};
        }
    };

    // Catálogo de NPCs hostiles: { npcIndex: { name, idHead, idBody } }.
    // El cliente resuelve el sprite combinando idBody/idHead con bodies.json
    // y heads.json. Solo entran hostiles con maxHit>0 que tienen spawn.
    loadHostileCatalog = async () => {
        try {
            const response = await fetch("/static/init/hostileCatalog.json");
            this.hostileCatalog = await response.json();
        } catch (e) {
            this.hostileCatalog = {};
        }
    };

    loadArmas = async () => {
        const response = await fetch("/static/init/armas.json");
        const result = await response.json();

        this.armas = result;
    };

    loadEscudos = async () => {
        const response = await fetch("/static/init/escudos.json");
        const result = await response.json();

        this.escudos = result;
    };

    loadCascos = async () => {
        const response = await fetch("/static/init/cascos.json");
        const result = await response.json();

        this.cascos = result;
    };

    loadFxs = async () => {
        const response = await fetch("/static/init/fxs.json");
        const result = await response.json();

        this.fxs = result;
    };
}

const inits = new Inits();

export default inits;
